/**
 * Batches all of a chart's text into a small number of MSDF meshes.
 *
 * A Pixi `BitmapText` with an MSDF font builds one unbatchable mesh (custom
 * shader program) per label, so a chart with dozens of labels costs dozens of
 * draw calls on every rendered frame. This builder instead lays out glyph
 * quads directly (mirroring `BitmapText.updateText`) and aggregates them into
 * one mesh per font atlas page, so an entire chart's text renders in a handful
 * of draw calls.
 *
 * Different font sizes (and max-width shrink factors) share a mesh: the
 * per-glyph font scale is stored in the `aFWidthScale` vertex attribute and
 * combined with the mesh-level `uFWidth` uniform in the shader (see
 * `chartTextShader.ts`).
 */

import { msdfFrag, msdfVert } from '@/app/gridGL/charts/chartTextShader';
import { emojis, EMOJI_ADVANCE_RATIO, EMOJI_X_OFFSET_RATIO, SCALE_EMOJI } from '@/app/gridGL/pixiApp/emojis/emojis';
import type { DrawOp, TextHAlign, TextVAlign } from '@/app/quadratic-core-types';
import {
  extractCharCode,
  splitTextToCharacters,
} from '@/app/web-workers/renderWebWorker/worker/cellsLabel/bitmapTextUtils';
import type { Renderer, Texture } from 'pixi.js';
import { BitmapFont, BLEND_MODES, Mesh, MeshGeometry, MeshMaterial, Program, Sprite } from 'pixi.js';

type TextOp = Extract<DrawOp, { type: 'Text' }>;

// Emojis render at the cell-text ratio (LINE_HEIGHT 16 for DEFAULT_FONT_SIZE 14)
// so they look the same size in chart labels as they do in the grid.
const EMOJI_SIZE_RATIO = 16 / 14;

export function chartFontName(bold: boolean, italic: boolean): string {
  if (bold && italic) return 'OpenSans-BoldItalic';
  if (bold) return 'OpenSans-Bold';
  if (italic) return 'OpenSans-Italic';
  return 'OpenSans';
}

interface Bucket {
  pageTexture: Texture;
  distanceFieldRange: number;
  vertices: number[];
  uvs: number[];
  colors: number[];
  fWidthScales: number[];
  indices: number[];
}

/** A glyph laid out in font-atlas units, before scaling/anchoring. */
interface GlyphPlacement {
  texture: Texture;
  page: number;
  fontName: string;
  x: number;
  y: number;
}

/** An emoji laid out in font-atlas units, before scaling/anchoring. */
interface EmojiPlacement {
  emoji: string;
  /** center of the emoji box */
  x: number;
  y: number;
  size: number;
}

function anchorFor(hAlign: TextHAlign, vAlign: TextVAlign): { x: number; y: number } {
  const x = hAlign === 'Left' ? 0 : hAlign === 'Center' ? 0.5 : 1;
  const y = vAlign === 'Top' ? 0 : vAlign === 'Middle' ? 0.5 : 1;
  return { x, y };
}

/** Mesh of chart-text glyphs sharing one font atlas page. */
class ChartTextMesh extends Mesh {
  private distanceFieldRange: number;

  constructor(bucket: Bucket) {
    const geometry = new MeshGeometry();
    geometry.addAttribute('aVertexPosition', new Float32Array(bucket.vertices), 2);
    geometry.addAttribute('aTextureCoord', new Float32Array(bucket.uvs), 2);
    geometry.addAttribute('aColors', new Float32Array(bucket.colors), 4);
    geometry.addAttribute('aFWidthScale', new Float32Array(bucket.fWidthScales), 1);
    geometry.addIndex(bucket.indices);

    const material = new MeshMaterial(bucket.pageTexture, {
      program: Program.from(msdfVert, msdfFrag),
      uniforms: { uFWidth: 0, uAlpha: 1 },
    });

    super(geometry, material);
    this.blendMode = BLEND_MODES.NORMAL_NPM;
    this.distanceFieldRange = bucket.distanceFieldRange;
  }

  // The MSDF edge width depends on the on-screen scale, which changes as the
  // viewport zooms; recompute it each render (same as pixi's BitmapText).
  // worldAlpha must be forwarded manually since the custom program doesn't use
  // MeshMaterial's uColor uniform.
  _render(renderer: Renderer) {
    const { a, b, c, d } = this.worldTransform;
    const dx = Math.sqrt(a * a + b * b);
    const dy = Math.sqrt(c * c + d * d);
    const worldScale = (Math.abs(dx) + Math.abs(dy)) / 2;
    this.shader.uniforms.uFWidth = worldScale * this.distanceFieldRange * renderer.resolution;
    this.shader.uniforms.uAlpha = this.worldAlpha;
    super._render(renderer);
  }
}

export class ChartTextBuilder {
  private buckets: Map<string, Bucket> = new Map();
  private sprites: Sprite[] = [];

  /** Lays out a Text op's glyphs and adds their quads to the shared buckets. */
  add(op: TextOp) {
    if (!op.text) return;
    const fontName = chartFontName(op.bold, op.italic);
    const font = BitmapFont.available[fontName];
    if (!font) {
      console.warn(`[ChartTextBuilder] Missing BitmapFont "${fontName}"`);
      return;
    }

    // layout in font-atlas units, mirroring BitmapText.updateText (left
    // aligned, no word wrap; splits on newlines)
    const scale = op.size / font.size;
    const text = op.text.replace(/(?:\r\n|\r)/g, '\n');
    const charsInput = splitTextToCharacters(text);
    const glyphs: GlyphPlacement[] = [];
    const emojiPlacements: EmojiPlacement[] = [];
    // emoji size/advance in atlas units, matching CellLabel's cell rendering
    const emojiSize = font.size * EMOJI_SIZE_RATIO;
    const emojiAdvance = emojiSize * EMOJI_ADVANCE_RATIO;
    let posX = 0;
    let posY = 0;
    let prevCharCode: number | null = null;
    let lastLineWidth = 0;
    let lastBreakWidth = 0;
    let maxLineWidth = 0;

    for (let i = 0; i < charsInput.length; i++) {
      const char = charsInput[i];
      if (/(?:\s)/.test(char)) {
        lastBreakWidth = lastLineWidth;
      }
      if (char === '\n') {
        maxLineWidth = Math.max(maxLineWidth, lastLineWidth);
        lastLineWidth = 0;
        posX = 0;
        posY += font.lineHeight;
        prevCharCode = null;
        continue;
      }
      const charCode = extractCharCode(char);
      const charData = font.chars[charCode];
      if (!charData) {
        // not a font glyph: render known emojis via the emoji spritesheet
        // (same assets as cell text), skip everything else
        let emoji = emojis.hasEmoji(char) ? char : undefined;
        if (!emoji && char.length === 1 && charsInput[i + 1] === '\uFE0F' && emojis.hasEmoji(char + '\uFE0F')) {
          // handles splitters that separate a variation selector (❤ + U+FE0F)
          emoji = char + '\uFE0F';
          i++;
        }
        if (emoji) {
          const x = posX + emojiSize * EMOJI_X_OFFSET_RATIO;
          emojiPlacements.push({ emoji, x: x + emojiSize / 2, y: posY + font.lineHeight / 2, size: emojiSize });
          lastLineWidth = x + emojiSize;
          posX += emojiAdvance;
          prevCharCode = null;
        }
        continue;
      }
      if (prevCharCode && charData.kerning[prevCharCode]) {
        posX += charData.kerning[prevCharCode];
      }
      const x = Math.round(posX + charData.xOffset);
      const y = Math.round(posY + charData.yOffset);
      glyphs.push({ texture: charData.texture, page: charData.page, fontName, x, y });
      lastLineWidth = x + Math.max(charData.xAdvance - charData.xOffset, charData.texture.orig.width);
      posX += charData.xAdvance;
      prevCharCode = charCode;
    }
    const lastChar = charsInput[charsInput.length - 1];
    if (lastChar !== '\n' && /(?:\s)/.test(lastChar)) {
      lastLineWidth = lastBreakWidth;
    }
    maxLineWidth = Math.max(maxLineWidth, lastLineWidth);

    const textWidth = maxLineWidth * scale;
    const textHeight = (posY + font.lineHeight) * scale;

    // shrink-to-fit, matching the previous BitmapText behavior
    // (`text.scale.set(max_width / text.width)`)
    const shrink =
      op.max_width !== null && op.max_width !== undefined && textWidth > op.max_width ? op.max_width / textWidth : 1;
    const fWidthScale = scale * shrink;

    const anchor = anchorFor(op.h_align, op.v_align);
    const anchorX = anchor.x * textWidth;
    const anchorY = anchor.y * textHeight;

    const rotation = op.rotation ? (op.rotation * Math.PI) / 180 : 0;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    const r = ((op.color >> 16) & 0xff) / 255;
    const g = ((op.color >> 8) & 0xff) / 255;
    const b = (op.color & 0xff) / 255;

    for (const glyph of glyphs) {
      const frame = glyph.texture.frame;
      // glyph quad relative to the anchor point, with shrink applied
      const left = (glyph.x * scale - anchorX) * shrink;
      const top = (glyph.y * scale - anchorY) * shrink;
      const right = left + frame.width * fWidthScale;
      const bottom = top + frame.height * fWidthScale;

      const bucket = this.getBucket(glyph);
      const vertexIndex = bucket.vertices.length / 2;

      // corners in TL, TR, BR, BL order (matching the glyph texture's uvs)
      const corners = [left, top, right, top, right, bottom, left, bottom];
      for (let i = 0; i < 8; i += 2) {
        const x = corners[i];
        const y = corners[i + 1];
        bucket.vertices.push(op.x + x * cos - y * sin, op.y + x * sin + y * cos);
        bucket.colors.push(r, g, b, op.alpha);
        bucket.fWidthScales.push(fWidthScale);
      }
      const uvs = glyph.texture._uvs.uvsFloat32;
      for (let i = 0; i < 8; i++) {
        bucket.uvs.push(uvs[i]);
      }
      bucket.indices.push(vertexIndex, vertexIndex + 1, vertexIndex + 2, vertexIndex, vertexIndex + 2, vertexIndex + 3);
    }

    // emojis are color sprites from the emoji spritesheet, not MSDF glyphs;
    // they get the same anchor/shrink/rotation transform as the glyph quads
    for (const placement of emojiPlacements) {
      const texture = emojis.getCharacter(placement.emoji);
      if (!texture) continue;
      const cx = (placement.x * scale - anchorX) * shrink;
      const cy = (placement.y * scale - anchorY) * shrink;
      const sprite = new Sprite(texture);
      // spritesheet textures pad each emoji to SCALE_EMOJI of its box; scale
      // up so the drawn emoji fills the layout box (same as drawEmoji)
      const size = (placement.size * fWidthScale) / SCALE_EMOJI;
      sprite.width = size;
      sprite.height = size;
      sprite.anchor.set(0.5);
      sprite.rotation = rotation;
      sprite.alpha = op.alpha;
      sprite.position.set(op.x + cx * cos - cy * sin, op.y + cx * sin + cy * cos);
      this.sprites.push(sprite);
    }
  }

  private getBucket(glyph: GlyphPlacement): Bucket {
    const font = BitmapFont.available[glyph.fontName];
    const pageTexture = font.pageTextures[glyph.page];
    const key = `${glyph.fontName}:${glyph.page}`;
    let bucket = this.buckets.get(key);
    if (!bucket) {
      bucket = {
        pageTexture,
        distanceFieldRange: font.distanceFieldRange,
        vertices: [],
        uvs: [],
        colors: [],
        fWidthScales: [],
        indices: [],
      };
      this.buckets.set(key, bucket);
    }
    return bucket;
  }

  /**
   * Creates the display objects for all added text: one mesh per font atlas
   * page containing all glyphs, plus a sprite per emoji (added after the
   * meshes so emojis draw above neighboring glyphs).
   */
  createDisplayObjects(): (Mesh | Sprite)[] {
    const objects: (Mesh | Sprite)[] = [];
    for (const bucket of this.buckets.values()) {
      if (bucket.vertices.length === 0) continue;
      objects.push(new ChartTextMesh(bucket));
    }
    return objects.concat(this.sprites);
  }
}
